import {
  useGetDemoQuery,
  useGetModificationsQuery,
  useGetWorkspaceQuery,
  useResetDemoMutation,
  useSaveDraftMutation,
  useUpdateDemoMutation,
} from '@carboncopyco/data';
import type { IEditorDraft } from '@carboncopyco/types';
import { RecordingTypes } from '@carboncopyco/types';
import { LoadingDots, Tooltip, notify } from '@carboncopyco/ui';
import cloneDeep from 'lodash/cloneDeep';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTabs } from '../../../store/context';
import SaveDemo from './SaveDemo';

interface PublishDemoProps {
  draft?: IEditorDraft;
  buttonRef: React.RefObject<any>;
  onClose: () => void;
  onExit: () => void;
  saveOpen: () => void;
  visible: boolean;
  demoName?: string;
  recordingType: RecordingTypes;
  organizationId: string;
  workspaceId: string;
  demoId?: string;
  workspacesFlag: boolean;
  onSavingDraftStarted?: () => void;
  onSavingDraftFinished?: () => void;
}
const PublishDemoModal = ({
  draft,
  buttonRef,
  onClose,
  onExit,
  visible,
  saveOpen,
  demoName,
  recordingType,
  organizationId,
  workspaceId,
  demoId,
  workspacesFlag,
  onSavingDraftStarted,
  onSavingDraftFinished,
}: PublishDemoProps) => {
  const { data: modifications } = useGetModificationsQuery(
    {
      organizationId,
      entityType: 'draft',
      entityId: String(draft?.id),
      apiVersion: 2,
    },
    { skip: !draft }
  );
  const edits = useMemo(
    () =>
      modifications?.filter(
        ({ modificationType, isDraft }) =>
          modificationType === 'edit' && isDraft
      ) ?? [],
    [modifications]
  );
  const [
    saveDraft,
    { isSuccess: isSuccessSavingDraft, isLoading: isLoadingSaveDraft },
  ] = useSaveDraftMutation();
  const { data: workspace, isLoading: isLoadingWorkspace } =
    useGetWorkspaceQuery(
      { organizationId, id: workspaceId },
      { skip: !workspacesFlag || !workspaceId }
    );
  const [demoDescription, setDemoDescription] = useState('');
  const { data: demo } = useGetDemoQuery(
    { organizationId, workspaceId, id: demoId || '' },
    { skip: !demoId }
  );
  const { globalTabs, setGlobalTabs, activeGlobalTabId } = useTabs();

  const [updateDemo] = useUpdateDemoMutation();

  useEffect(() => {
    const rules = draft?.rules?.length ? cloneDeep(draft.rules) : [];
    if (rules.length) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rules.forEach((rule) => delete rule.ruleId);
    }
  }, [edits, draft, demo]);

  const isSaved = isSuccessSavingDraft && !draft?.modified;

  const handlePublishDemo = (form: Record<string, string>) => {
    const updatedGlobalTabs = globalTabs.map((tab) =>
      tab.id === activeGlobalTabId
        ? {
            ...tab,
            title: form.demoName,
            payload: {
              ...tab.payload,
              demoName: form.demoName,
              draftModified: false,
            },
          }
        : tab
    );
    setGlobalTabs(updatedGlobalTabs);
    const newDescription = form.description;

    setDemoDescription(newDescription);
    if (demoId) {
      updateDemo({
        organizationId,
        workspaceId,
        id: demoId,
        demoName: form.demoName || 'Untitled overlay',
      });
    }
    onSavingDraftStarted?.();
    return saveDraft({
      organizationId,
      draftId: (draft?.id || '').toString(),
      comment: newDescription,
    })
      .unwrap()
      .then(() => {
        notify.success(
          `Your ${
            recordingType === RecordingTypes.overlay ? 'overlay' : ''
          } demo was saved successfully!`
        );
        onSavingDraftFinished?.();
        onClose();
        return null;
      })
      .catch(() => {
        notify.error('Error occured while saving demo');
      });
  };

  const [resetDemo] = useResetDemoMutation();
  useEffect(() => {
    return () => {
      if (demoId) resetDemo({ id: demoId });
    };
  }, []);

  return visible ? (
    <Tooltip
      onClickOutside={isSaved || isLoadingSaveDraft ? saveOpen : onClose}
      visible={visible}
      anchorRef={buttonRef}
      noArrow
      placement="bottom-end"
      interactive
    >
      <div className="p-5">
        {isLoadingSaveDraft || isLoadingWorkspace ? (
          <div className="justify-center flex w-ds-sm-modal h-ds-sm-modal">
            <LoadingDots />
          </div>
        ) : (
          <>
            {workspace && (
              <SaveDemo
                onCancel={onClose}
                onExit={onExit}
                publishDemo={handlePublishDemo}
                demoName={demoName}
                demoDescription={demoDescription}
                workspace={workspace}
                editsNumber={edits.length}
              />
            )}
          </>
        )}
      </div>
    </Tooltip>
  ) : null;
};

export default PublishDemoModal;
